import { useMemo } from "react";
import { EventLateFlags } from "./useEventLateFlags";

export interface TimelineEventCalculation {
  leftPx: number;
  widthPx: number;
  shouldRender: boolean;
}

/**
 * Verifica se deve mostrar o ícone de atrasado para o evento
 * Usa flags calculados pela função centralizada
 * 
 * IMPORTANTE: Considera tanto o status atual quanto o originalStatus
 * para mostrar ícone de atraso mesmo quando a tolerância corrige o status.
 * Ex: Backend retorna STARTED_LATE, tolerância corrige para STARTED,
 *     mas ainda mostra ícone de atraso (US 2298696)
 */
export function shouldShowLateIcon(event: Sentinel.Timeline.Event, lateFlags: EventLateFlags): boolean {
  const status = event.status;
  const originalStatus = event.originalStatus;

  // CANCELED: não mostra ícone
  if (status === "CANCELED") {
    return false;
  }

  // Status que a API já indica como atrasado → sempre mostra ícone
  // Verifica AMBOS status (atual e original) para capturar casos onde
  // a tolerância corrigiu o status mas ainda houve atraso
  const lateStatuses: Sentinel.Enumerators.ProcessExecutionStatus[] = ["COMPLETED_LATE", "DELAYED", "STARTED_LATE"];
  if ((status && lateStatuses.includes(status)) ||
    (originalStatus && lateStatuses.includes(originalStatus))) {
    return true;
  }

  // Outros status: verifica se passou do fim previsto comparando com a hora atual
  return lateFlags.notStartedAndEndPassed || lateFlags.endedLate || lateFlags.onlyEndLate || lateFlags.awaitingEndLate;
}

const MINUTES_MS = 1000 * 60;

/**
 * Calcula apenas posição (leftPx, widthPx) pois depende do container.
 */
export function calculateTimelineEvent(
  event: Sentinel.Timeline.Event | undefined,
  start: Date,
  end: Date,
  containerSize: number
): TimelineEventCalculation {
  if (!event?.calculatedStart || !event?.calculatedEnd) {
    return {
      leftPx: 0,
      widthPx: 0,
      shouldRender: false,
    };
  }

  const startMs = start.getTime();
  const endMs = end.getTime();
  const totalMinutes = (endMs - startMs) / MINUTES_MS;

  const sMs = new Date(event.calculatedStart).getTime();
  const eMs = new Date(event.calculatedEnd).getTime();

  // Evento termina antes do início OU começa depois do fim = não renderiza
  if (eMs <= startMs || sMs >= endMs) {
    return {
      leftPx: 0,
      widthPx: 0,
      shouldRender: false,
    };
  }

  // Ajustar para mostrar apenas a parte visível dentro do range
  const visibleStart = Math.max(sMs, startMs);
  const visibleEnd = Math.min(eMs, endMs);

  const startInMinutes = (visibleStart - startMs) / MINUTES_MS;
  const durationInMinutes = (visibleEnd - visibleStart) / MINUTES_MS;

  const leftPx = (startInMinutes / totalMinutes) * containerSize;
  const widthPx = (durationInMinutes / totalMinutes) * containerSize;

  return {
    leftPx,
    widthPx,
    shouldRender: true,
  };
}

/**
 * Hook para calcular posição e status de atraso de eventos na timeline
 */
export function useTimelineEvent(
  event: Sentinel.Timeline.Event | undefined,
  start: Date,
  end: Date,
  containerSize: number
): TimelineEventCalculation {
  return useMemo(
    () => calculateTimelineEvent(event, start, end, containerSize),
    [event, start, end, containerSize]
  );
}
